// React Query hooks for license operations.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from './licenses.api';

export const LICENSES_KEY = 'licenses';

export function useLicenses(query: api.ListLicensesQuery) {
  return useQuery({
    queryKey: [LICENSES_KEY, query],
    queryFn: () => api.listLicenses(query),
    placeholderData: (prev) => prev,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [LICENSES_KEY] });
}

export function useIssueLicense() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: api.IssueLicenseInput) => api.issueLicense(input),
    onSuccess: invalidate,
  });
}

export function useRevokeLicense() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => api.revokeLicense(id, reason),
    onSuccess: invalidate,
  });
}

export function useExtendLicense() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, addDays }: { id: string; addDays: number }) => api.extendLicense(id, addDays),
    onSuccess: invalidate,
  });
}

export function useRegenerateKey() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.regenerateKey(id),
    onSuccess: invalidate,
  });
}

export function useUpdateLicense() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<api.IssueLicenseInput> }) =>
      api.updateLicense(id, input),
    onSuccess: invalidate,
  });
}
